/**
 * Social media OAuth connect service.
 * Opens OAuth popup for each platform and returns the connected account info.
 */

const NAVER_CLIENT_ID = process.env.REACT_APP_NAVER_CLIENT_ID || '';
const FACEBOOK_APP_ID = process.env.REACT_APP_FACEBOOK_APP_ID || '';
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
// Dedicated YouTube OAuth client (project lucid-arc-493209-v9, where YouTube
// Data API v3 is enabled). Must match the backend YOUTUBE_OAUTH_CLIENT_ID.
// NO fallback to the shared Google login client: that client lives in project
// 996002701005 where the YouTube API is disabled, so falling back would silently
// send the call to the wrong project and fail with a misleading error.
const YOUTUBE_OAUTH_CLIENT_ID = process.env.REACT_APP_YOUTUBE_OAUTH_CLIENT_ID || '';
const REDIRECT_URI = `${window.location.origin}/social-connect/callback`;

interface SocialConnectResult {
  platform: string;
  username: string;
  email?: string;
  accessToken?: string;
  // Populated for YouTube connect (channels.list?mine=true).
  channelId?: string;
  title?: string;
  handle?: string;
  subscriberCount?: number | null;
  hiddenSubscriberCount?: boolean | null;
}

/**
 * Open OAuth popup and return a promise that resolves with the account info.
 */
function openOAuthPopup(url: string, platform: string): Promise<SocialConnectResult> {
  return new Promise((resolve, reject) => {
    const width = 500;
    const height = 600;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const popup = window.open(
      url,
      `${platform}_connect`,
      `width=${width},height=${height},left=${left},top=${top},scrollbars=yes`
    );

    if (!popup) {
      reject(new Error('Popup blocked'));
      return;
    }

    // Listen for message from callback page
    const handler = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'social-connect-result' && event.data?.platform === platform) {
        window.removeEventListener('message', handler);
        if (event.data.error) {
          reject(new Error(event.data.error));
        } else {
          resolve(event.data.result as SocialConnectResult);
        }
      }
    };

    window.addEventListener('message', handler);

    // Check if popup was closed without completing
    const checkClosed = setInterval(() => {
      if (popup.closed) {
        clearInterval(checkClosed);
        window.removeEventListener('message', handler);
        reject(new Error('Popup closed'));
      }
    }, 500);
  });
}

export const socialConnectService = {
  /**
   * Connect Naver Blog via Naver OAuth.
   * Scopes: profile, email
   */
  async connectNaver(): Promise<SocialConnectResult> {
    // Encode the platform into the state so the popup callback can detect it
    // without relying on the opener's sessionStorage (separate browsing context).
    const state = `naverblog_${Math.random().toString(36).substring(7)}`;
    sessionStorage.setItem('naver_oauth_state', state);
    const url = `https://nid.naver.com/oauth2.0/authorize?response_type=code&client_id=${NAVER_CLIENT_ID}&redirect_uri=${encodeURIComponent(REDIRECT_URI)}&state=${state}&scope=profile`;
    return openOAuthPopup(url, 'naverblog');
  },

  /**
   * Connect Instagram via Facebook OAuth.
   * Scopes: instagram_basic, pages_show_list
   */
  async connectInstagram(): Promise<SocialConnectResult> {
    const state = `instagram_${Math.random().toString(36).substring(7)}`;
    sessionStorage.setItem('instagram_oauth_state', state);
    const url = `https://www.facebook.com/v18.0/dialog/oauth?client_id=${FACEBOOK_APP_ID}&redirect_uri=${encodeURIComponent(REDIRECT_URI)}&state=${state}&scope=instagram_basic,pages_show_list&response_type=code`;
    return openOAuthPopup(url, 'instagram');
  },

  /**
   * Connect YouTube via Google OAuth.
   * Scopes: youtube.readonly, userinfo.email
   */
  async connectYoutube(): Promise<SocialConnectResult> {
    if (!YOUTUBE_OAUTH_CLIENT_ID) {
      throw new Error(
        'YouTube 연결이 설정되지 않았습니다. REACT_APP_YOUTUBE_OAUTH_CLIENT_ID (project lucid-arc-493209-v9) 를 설정한 뒤 다시 시도해주세요.',
      );
    }
    const state = `youtube_${Math.random().toString(36).substring(7)}`;
    sessionStorage.setItem('google_oauth_state', state);
    const url = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${YOUTUBE_OAUTH_CLIENT_ID}&redirect_uri=${encodeURIComponent(REDIRECT_URI)}&state=${state}&scope=https://www.googleapis.com/auth/youtube.readonly https://www.googleapis.com/auth/userinfo.email&response_type=code&access_type=offline&prompt=consent`;
    return openOAuthPopup(url, 'youtube');
  },
};
